import React, { useEffect } from 'react';
import { Flex, Heading, Link, Text } from '@chakra-ui/react';
import { SdgCategoryHeader } from './SdgCategoryHeader';
import { SdgImageContainer } from './SdgImageContainer';
import { FormData } from './SdgQuiz';
import { calculateSDGRelevance, countSdgs, useResponsive } from '../utils';
import contentJson from '../utils/content.json';

const content: any = contentJson;

export type FinishPageProps = {
  data: any;
  updateFields: (fields: Partial<FormData>) => void;
};

export const FinishPage = ({ data, updateFields }: FinishPageProps) => {
  const numberOfSdgs = countSdgs(content);

  const sdgRelevance = calculateSDGRelevance(content, data, numberOfSdgs);

  const isMobileScreen = useResponsive();

  useEffect(() => {
    updateFields({
      results_very_relevant: sdgRelevance.relevant.join(','),
      results_partly_relevant: sdgRelevance.partialRelevant.join(','),
      results_not_very_relevant: sdgRelevance.notRelevant.join(','),
      results_skipped: sdgRelevance.skipped.join(','),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex
      flexDir="column"
      justifyContent="center"
      alignItems="center"
      width="100%"
    >
      <Flex
        flexDir="column"
        justifyContent="center"
        alignItems="center"
        maxW="28rem"
      >
        <Heading
          as="h1"
          textAlign="center"
          my={5}
          fontSize={isMobileScreen ? 'xl' : '4xl'}
        >
          You’re done! Here are your results.
        </Heading>
        <Text fontSize={isMobileScreen ? 'sm' : 'md'} textAlign="center">
          Ready to participate in the Impact Rankings? <br />
          Watch our recent{' '}
          <Link
            textDecoration="underline"
            target="_blank"
            href="https://www.timeshighereducation.com/content/impact-rankings-2025-masterclass-series-2"
          >
            Impact participation masterclass.
          </Link>
          <br />
          Still not sure? Learn more about the{' '}
          <Link
            textDecoration="underline"
            target="_blank"
            href="https://www.timeshighereducation.com/our-solutions/data-and-insights/sdg-impact-dashboard"
          >
            SDG Impact Dashboard,
          </Link>{' '}
          a powerful data and analytics tool.
        </Text>
      </Flex>

      <Flex
        flexDir="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        maxW="50rem"
      >
        {Object.entries(sdgRelevance).map(([relevanceType, sdgs]) =>
          sdgs.length ? (
            <Flex
              key={relevanceType}
              alignItems="center"
              flexDirection="column"
              width="100%"
            >
              <SdgCategoryHeader
                title={content.finalRelevance[relevanceType].finishTitle}
                backgroundColor={
                  content.finalRelevance[relevanceType].finishColor
                }
                color="white"
              />

              <Text
                maxW="46rem"
                align="center"
                fontSize={'sm'}
                color="gray.600"
              >
                {content.finalRelevance[relevanceType].finishDescription}
              </Text>

              <Flex
                width="100%"
                maxW="40rem"
                justifyContent="space-evenly"
                flexWrap="wrap"
                my={8}
              >
                {sdgs.map((sdg) => (
                  <SdgImageContainer
                    key={sdg}
                    relevanceType={relevanceType}
                    bgPosition={
                      isMobileScreen
                        ? content[`sdg${sdg}`].imgSpritePositionMobileLarge
                        : content[`sdg${sdg}`].imgSpritePositionDesktop
                    }
                    bgSize={
                      isMobileScreen
                        ? content.imgContainerSizes.imgSizeMobileLarge
                        : content.imgContainerSizes.imgSizeDesktop
                    }
                    imgWidth={
                      isMobileScreen
                        ? content.imgContainerSizes.imgSideMobileLarge
                        : content.imgContainerSizes.imgSideDesktop
                    }
                    imgHeight={
                      isMobileScreen
                        ? content.imgContainerSizes.imgSideMobileLarge
                        : content.imgContainerSizes.imgSideDesktop
                    }
                  />
                ))}
              </Flex>
            </Flex>
          ) : null,
        )}
      </Flex>
    </Flex>
  );
};
